import * as Api from '@ViewModels';
import { IImpersonationContext } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useSelectionState } from '../../../../hooks/useSelectionState';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { invalidateAllTagFilterQueries, useGenerateTagReportMutation } from '../../../../queries/Tag';
import { Row } from '../../../components-v2/PageLayout';
import { Button, ButtonBase } from '../../../components/Button';
import { Databoard, DataboardActions, DataboardFilters } from '../../../components/Databoard';
import { TextInput } from '../../../components/TextInput';
import { AddTagModal } from '../../../components/reporting/tags/AddTag';
import { GenerateTagNotificationsReportModal } from '../../../components/reporting/tags/GenerateTagNotificationsReport';
import { SearchIcon } from '../../../components/svgs/icons/SearchIcon';
import { ContactsTableLoader } from '../shared/ContactsTableLoader';
import { CreateCategoryModal } from './TagCategoryModals/CreateCategoryModal';
import { DeleteCategoryModal } from './TagCategoryModals/DeleteCategoryModal';
import { UpdateCategoryModal } from './TagCategoryModals/UpdateCategoryModal';
import { TagsActionsBar } from './TagsActionsBar';
import { useTagsDataContext } from './TagsContexts/TagsDataContext';
import { useTagsSearchContext } from './TagsContexts/TagsSearchContext';
import { TagsSearchDescription } from './TagsSearchDescription';
import { TagsTable } from './TagsTable/TagsTable';
import { getTagSearchFromCriteria } from './TagsUtilities';
import { styleSheet } from './styles';

export const TagsMain: React.FC<{
	isAdminContext?: boolean;
	impersonationContext?: IImpersonationContext;
}> = props => {
	const { isAdminContext = false, impersonationContext } = props;
	const errorMessages = useErrorMessages();
	const { initialSearchValue, filter, debouncedSetSearchTerm, searchTerm } = useTagsSearchContext();
	const { tags, categories, selectedTag, selectedCategory, setSelectedCategory } = useTagsDataContext();

	// Table state
	const selectionState = useSelectionState({ rows: tags?.data?.map(item => item.id) });

	// Modals
	const [showAddCategoryModal, setShowAddCategoryModal] = React.useState(false);
	const [showAddTagModal, setShowAddTagModal] = React.useState(false);
	const [showDeleteCategoryModal, setShowDeleteCategoryModal] = React.useState(false);
	const [showUpdateCategoryModal, setShowUpdateCategoryModal] = React.useState(false);
	const [showGenerateReportModal, setShowGenerateReportModal] = React.useState(false);

	const searchInputRef = React.useRef<HTMLInputElement>(null);

	const generateTagReportMutation = useGenerateTagReportMutation({
		impersonationContext,
		onSuccess: () => {
			toast.success('Tag report has been initiated. You will receive an email when the report is ready.');
		},
		onError: error => {
			toast.error('An error occurred while trying to generate a tag report.');
			errorMessages.pushApiError(error);
		},
	});

	// Update states when query changes
	React.useEffect(() => {
		if (!filter?.criteria) return;
		if (searchInputRef.current) {
			searchInputRef.current.value = getTagSearchFromCriteria(filter.criteria);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	// Event handlers for tag actions
	const handleAddTagClick = () => {
		setShowAddTagModal(true);
	};

	const handleAddTagModalClose = (tag?: Api.TagViewModel, canceled?: boolean) => {
		setShowAddTagModal(false);
		if (!canceled && tag) {
			invalidateAllTagFilterQueries();
		}
	};

	const handleGenerateReportClick = () => {
		setShowGenerateReportModal(true);
	};

	const handleGenerateReportModalClose = (reportParams?: any, canceled?: boolean) => {
		setShowGenerateReportModal(false);

		if (!canceled && reportParams) {
			generateTagReportMutation.mutate(reportParams);
		}
	};

	const onSearch = () => {
		const value = searchInputRef.current?.value || '';
		debouncedSetSearchTerm(value);
		selectionState.reset();
	};

	const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			onSearch();
		}
	};

	const clearSearch = () => {
		if (searchInputRef.current) {
			searchInputRef.current.value = '';
		}
		if (searchTerm) debouncedSetSearchTerm('');
		selectionState.reset();
	};

	const handleCategoryCreated = (category: Api.ITagCategory | null) => {
		if (category) {
			setSelectedCategory(category);
		}
	};

	return (
		<>
			<Databoard styles={[styleSheet.databoard]}>
				<DataboardFilters>
					<Row styles={[styleSheet.searchControls]}>
						<div className={css(styleSheet.leftActions)}>
							<div className={css(styleSheet.searchContainer)}>
								<TextInput
									type='text'
									placeholder='Search'
									defaultValue={initialSearchValue}
									className={css(styleSheet.inputContainer)}
									onKeyDown={onSearchKeyDown}
									inputRef={searchInputRef}
								/>
								<ButtonBase className={css(styleSheet.searchButton)} onClick={onSearch}>
									<SearchIcon className={css(styleSheet.searchIcon)} fillColor='white' />
								</ButtonBase>
							</div>
							{!!searchTerm && (
								<Button kind='reverse' onClick={clearSearch} styles={[styleSheet.noWrap]}>
									Clear search
								</Button>
							)}
						</div>
						<div className={css(styleSheet.rightActions)}>
							{!selectedCategory ? (
								<>
									<Button kind='primary' onClick={handleAddTagClick} styles={[styleSheet.noWrap]}>
										Add Tag
									</Button>
									<Button kind='primary' onClick={() => setShowAddCategoryModal(true)} styles={[styleSheet.noWrap]}>
										Add Category
									</Button>
								</>
							) : (
								<>
									<Button kind='destructive' onClick={() => setShowDeleteCategoryModal(true)}>
										Delete Category
									</Button>
									<Button kind='primary' styles={[styleSheet.noWrap]} onClick={() => setShowUpdateCategoryModal(true)}>
										Add Tag to Category
									</Button>
								</>
							)}
							{!isAdminContext ? (
								<Button kind='primary' onClick={handleGenerateReportClick}>
									Generate Report
								</Button>
							) : null}
						</div>
					</Row>
				</DataboardFilters>

				<DataboardFilters>
					<Row styles={[styleSheet.searchDescription]}>
						<TagsSearchDescription selectionState={selectionState} />
					</Row>
				</DataboardFilters>
				<DataboardActions>
					<TagsActionsBar
						impersonationContext={impersonationContext}
						isAdminContext={isAdminContext}
						selectionState={selectionState}
					/>
				</DataboardActions>
				{!tags?.data?.length && !categories?.data?.length && (tags.isFetching || categories.isFetching) ? (
					<ContactsTableLoader />
				) : (
					<TagsTable
						clearSearch={clearSearch}
						isAdminContext={isAdminContext}
						impersonationContext={impersonationContext}
						selectionState={selectionState}
						openUpdateCategoryModal={() => setShowUpdateCategoryModal(true)}
					/>
				)}
			</Databoard>
			<GenerateTagNotificationsReportModal
				modalProps={{
					isOpen: showGenerateReportModal,
					onRequestClose: handleGenerateReportModalClose,
				}}
			/>
			<AddTagModal
				modalProps={{
					isOpen: !!showAddTagModal,
					onRequestClose: handleAddTagModalClose,
				}}
				impersonationContext={impersonationContext}
			/>
			<CreateCategoryModal
				isOpen={showAddCategoryModal}
				onClose={() => setShowAddCategoryModal(false)}
				impersonationContext={impersonationContext}
				onCategoryCreated={handleCategoryCreated}
			/>
			<UpdateCategoryModal
				isOpen={showUpdateCategoryModal || !!selectedTag}
				onClose={() => setShowUpdateCategoryModal(false)}
				impersonationContext={impersonationContext}
			/>
			<DeleteCategoryModal
				isOpen={showDeleteCategoryModal}
				onClose={() => setShowDeleteCategoryModal(false)}
				impersonationContext={impersonationContext}
			/>
		</>
	);
};
